/**
 * @author: Anand Kumar
 * @date : 08-Aug-2020
 * File Description: Company Validation Schema
 */
import * as Yup from "yup";
import * as REGEX from "../../../config/regexValidation";
import { COMPANY_MAX_IMG_SIZE } from "../../../config";
import he from "he";

export const addressValidationSchema = {
  address1: Yup.string()
    .min(25, "Address must be at least 25 characters")

    .required("Please enter address"),
  countryName: Yup.string().required("Please select country name").typeError("Please select country name"),
};

export const fundingValidationSchema = {
  fundingName: Yup.string()
    .min(2, "Please enter transaction name of at least 2 characters")
    .max(125, "Please enter transaction name of maximum 125 characters")
    .required("Please enter transaction name"),
  fundingAmount: Yup.lazy((val) =>
    val === "NA"
      ? Yup.string().required("Enter NA")
      : Yup.number()
          .typeError("Funding amount should be NA or number")
          .required("Please enter funding amount")
          .max(999999, "Funding Amount cannot exceed 999,999")
  ),

  fundingTypeRound: Yup.string().required("Please select funding type"),
  fundingDate: Yup.date().typeError("Please select transaction date").nullable().default(null).required("Please select transaction date"),
  fundingInvestors: Yup.array().required("Please select funding investors"),
};

export const revenueValidationSchema = {
  revenueYear: Yup.string().required("Please enter revenue year").matches(REGEX.YEAR, "Please enter valid 4 digit year"),
  revenueAmount: Yup.lazy((val) =>
    val === "NA"
      ? Yup.string().required("Enter NA")
      : Yup.number().typeError("Revenue amount should be NA or number").required("Please enter revenue amount")
  ),
  // revenueCurrency: Yup.string().required("Please select revenue currency"),
  revenueSourceUrl: Yup.string().required("Please enter valid url").matches(REGEX.URL, "Please enter valid URL"),
};

export const investmentValidationSchema = {
  fundingName: Yup.string().max(125, "Please enter transaction name of maximum 125 characters").required("Please enter transaction name"),
  fundingAmount: Yup.lazy((val) =>
    val === "NA"
      ? Yup.string().required("Enter NA")
      : Yup.number()
          .typeError("Funding amount should be NA or number")
          .required("Please enter funding amount")
          .max(999999999999, "Funding Amount cannot exceed 999,999,999,999")
  ),
  fundingTypeRound: Yup.string().required("Please select funding type"),
  fundingDate: Yup.date().typeError("Please select transaction date").nullable().default(null).required("Please select transaction date"),
  fundingOnCompanyName: Yup.string().required("Please select funding company").nullable(),
};

// Utility to check for empty rich text like <p>&nbsp;</p>
const isOnlyNbspParagraph = (val) => typeof val === "string" && val.trim().toLowerCase() === "<p>&nbsp;</p>";

const stripHtmlAndGetTextLength = (html) => {
  if (typeof html !== "string") return 0;
  const withoutTags = html.replace(/<\/?[^>]+(>|$)/g, "");
  const decoded = he.decode(withoutTags);
  return decoded.trim().length;
};
export const advancedValidationSchema = {
  companyAbout: Yup.string()
    .test("companyAbout-required", "Please enter the description.", (val) => !isOnlyNbspParagraph(val))
    .test("companyAbout-min", "The minimum character limit is 25.", (val) => isOnlyNbspParagraph(val) || stripHtmlAndGetTextLength(val) >= 25)
    .test(
      "companyAbout-max",
      "You have exceeded the maximum character limit of 1000.",
      (val) => isOnlyNbspParagraph(val) || stripHtmlAndGetTextLength(val) <= 1000
    ),

  companyBusinessModel: Yup.string()
    .notRequired()
    .test("companyBusinessModel-min", "The minimum character limit is 25.", (val) => {
      return isOnlyNbspParagraph(val) || stripHtmlAndGetTextLength(val) === 0 || stripHtmlAndGetTextLength(val) >= 25;
    })
    .test(
      "companyBusinessModel-max",
      "You have exceeded the maximum character limit of 1000.",
      (val) => isOnlyNbspParagraph(val) || stripHtmlAndGetTextLength(val) <= 1000
    ),

  companyFuturePlans: Yup.string()
    .notRequired()
    .test("companyFuturePlans-min", "The minimum character limit is 25.", (val) => {
      return isOnlyNbspParagraph(val) || stripHtmlAndGetTextLength(val) === 0 || stripHtmlAndGetTextLength(val) >= 25;
    })
    .test(
      "companyFuturePlans-max",
      "You have exceeded the maximum character limit of 1000.",
      (val) => isOnlyNbspParagraph(val) || stripHtmlAndGetTextLength(val) <= 1000
    ),

  companyWhatsUnique: Yup.string()
    .notRequired()
    .test("companyWhatsUnique-max", "You have exceeded the maximum character limit of 200.", (val) => {
      if (!val || isOnlyNbspParagraph(val)) return true;
      return stripHtmlAndGetTextLength(val) <= 200;
    }),
};

// company Video Validation
export const videoValidationSchema = {
  videoTitle: Yup.string().min(2).required("Please enter video title"),
  videoDescription: Yup.string(),
  videoSource: Yup.string().typeError("Please select video source").required("Please select video source"),
  videoId: Yup.string().min(5).required("Please enter video id"),
  videoThumbnail: Yup.mixed().required("Please upload video thumbnail"),
  videoDurationInMinute: Yup.number()
    .typeError("Please enter a valid number")
    .required("Please enter video duration")
    .integer("Only whole numbers are allowed")
    .min(1, "Video duration must be at least 1 minute")
    .max(9999, "Video duration cannot exceed 9999 minutes"),
};
// company Image Validation
export const imageValidationSchema = {
  imageFile: Yup.mixed()
    .required("Please upload image")
    .test("imageFile", "File size is more than 5 MB", (value) => {
      return !value || (value && value[0].size <= COMPANY_MAX_IMG_SIZE);
    }),
};
// Company upload Excel Doc Validation
export const uploadDocValidationSchema = {
  uploadFor: Yup.string().required("Please select upload for"),
  selIndustry: Yup.string().required("Please select industry"),
  documentFile: Yup.mixed().required("Please upload document"),
};
// Screening rejection Company upload Excel Doc Validation
export const screeningUploadDocValidationSchema = {
  documentFile: Yup.mixed().required("Please upload document"),
};
// Company Document Brochure Validation
export const addDocumentValidationSchema = {
  documentTitle: Yup.string().required("Please enter document name"),
  documentDescription: Yup.string()
    .min(10, "Description must be at least 10 characters")
    .required("Please enter document description")
    .test(
      "documentDescription-max",
      "You have exceeded the maximum character limit of 1000.",
      (val) => isOnlyNbspParagraph(val) || stripHtmlAndGetTextLength(val) <= 1000
    ),
  documentFile: Yup.mixed().required("Please upload document"),
};
// Company Edit Document Brochure Validation
export const editDocumentValidationSchema = {
  documentTitle: Yup.string().required("Please enter document name"),
  documentDescription: Yup.string()
    .min(10, "Description must be at least 10 characters")
    .required("Please enter document description")
    .test(
      "documentDescription-max",
      "You have exceeded the maximum character limit of 1000.",
      (val) => isOnlyNbspParagraph(val) || stripHtmlAndGetTextLength(val) <= 1000
    ),
};

export const companyOverviewValidationSchema = {
  companyName: Yup.string()
    .required("Please enter company name")
    .max(125, "Company name can not be more than 125 characters")
    .matches(REGEX.COMPANY, "Company name must not contain HTML tags"),
  companyFoundedYear: Yup.string().matches(REGEX.YEAR, "Please enter valid Founded Year"), //Years from 1000 to 2999
  companySocialFacebook: Yup.string().matches(REGEX.FACEBOOK, "Please enter valid Facebook page URL"),
  companySocialLinkedIn: Yup.string().matches(REGEX.LINKEDIN, "Please enter valid LinkedIn page URL"),
  companyWebsite: Yup.string().required("Please enter company website").matches(REGEX.URL, "Please enter valid company website URL"),
  companyPublishedDate: Yup.string().required("Please select the publish date").nullable(),
};
